import React from 'react';
import { 
  Row, 
  Col, 
  Button, 
  Typography, 
  Card, 
  Space, 
  Carousel,
  Tag
} from 'antd';
import { 
  LeftOutlined, 
  RightOutlined,
  GiftOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const CartItemModal = ({ 
  cart, 
  currentIndex, 
  onNavigate 
}) => {
  const carouselRef = React.useRef();
  const currentItem = cart[currentIndex];
  
  const settings = {
    dots: true,
    arrows: false,
    autoplay: false,
  };
  
  let images = currentItem?.ARLR_Images_Path ? [...currentItem.ARLR_Images_Path] : [];
  if (currentItem?.ARLR_Image_Path) {
    images.unshift(currentItem.ARLR_Image_Path);
  }
  images = images.length === 0 ? [currentItem?.ARLR_Image_Path].filter(Boolean) : images;

  const nextItem = () => {
    onNavigate((prev) => (prev + 1) % cart.length);
  };

  const prevItem = () => {
    onNavigate((prev) => (prev - 1 + cart.length) % cart.length);
  };

  if (!currentItem) return null;

  return (
    <Card bordered={false} style={{ background: '#f7f7f7' }} bodyStyle={{ padding: '24px' }}>
      <Row gutter={[16, 16]}>
        {/* Left Column - Image Carousel */}
        <Col xs={24} md={12}>
          <Card 
            bordered={true}
            style={{ background: '#fff' }}
            bodyStyle={{ 
              padding: 0,
            }}
          >
            <div style={{ position: 'relative', lineHeight: 0 }}>
              <Button 
                type="text"
                icon={<LeftOutlined style={{ fontSize: '16px' }} />}
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  background: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  border: '1px solid #e8e8e8',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.3s',
                  ':hover': {
                    background: 'rgba(255, 255, 255, 1)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  }
                }}
                onClick={() => carouselRef.current?.prev()}
              />
              <Button 
                type="text"
                icon={<RightOutlined style={{ fontSize: '16px' }} />}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  background: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  border: '1px solid #e8e8e8',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.3s',
                  ':hover': {
                    background: 'rgba(255, 255, 255, 1)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  }
                }}
                onClick={() => carouselRef.current?.next()}
              />
              <Carousel ref={carouselRef} {...settings} style={{ marginBottom: '-6px' }}>
                {images.map((image, index) => (
                  <div key={index}>
                    <div style={{ 
                      height: '400px',
                      background: 'rgb(246, 248, 251)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '20px 20px 40px 20px',
                      lineHeight: 'normal'
                    }}>
                      <img 
                        style={{ 
                          maxHeight: '360px',
                          maxWidth: '100%',
                          objectFit: 'contain',
                        }} 
                        src={image} 
                        alt={`${currentItem.ARLR_Title}`} 
                      />
                    </div>
                  </div>
                ))}
              </Carousel>
            </div>
          </Card>
        </Col>

        {/* Right Column - Item Details */}
        <Col xs={24} md={12}>
          <Card 
            style={{ 
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}
            bodyStyle={{
              padding: '24px',
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%', flex: 1 }}>
              {/* Header */}
              <div>
                <Title level={4} style={{ marginBottom: 4 }}>
                  {currentItem.ARLR_Title}
                </Title>
                <Tag icon={<GiftOutlined />} color="blue">
                  In Cart
                </Tag>
              </div>

              {/* Points */}
              <Card size="small" style={{ background: '#f5f7ff' }}>
                <Row gutter={16} style={{ textAlign: 'center' }}>
                  <Col span={8}>
                    <div>
                      <div style={{ marginBottom: '4px', color: 'rgba(0, 0, 0, 0.45)' }}>Redeem Points</div>
                      <div style={{ fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <GiftOutlined style={{ marginRight: '4px' }} />
                        {currentItem.ARLR_Norm_Points}
                      </div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div>
                      <div style={{ marginBottom: '4px', color: 'rgba(0, 0, 0, 0.45)' }}>Total Redeem Points</div>
                      <div style={{ fontSize: '18px', color: '#1890ff' }}>
                        {currentItem.ARLR_Norm_Points * currentItem.qty}
                      </div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div>
                      <div style={{ marginBottom: '4px', color: 'rgba(0, 0, 0, 0.45)' }}>Quantity</div>
                      <div style={{ fontSize: '18px' }}>
                        {currentItem.qty}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>

              {/* Description */}
              <div>
                <Title level={5} style={{ marginBottom: 4, marginTop: 0 }}>Description</Title>
                <Card 
                  size="small" 
                  style={{ 
                    background: '#fafafa',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}
                >
                  <Text style={{ 
                    whiteSpace: 'pre-wrap',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    display: 'block'
                  }}>
                    {currentItem.ARLR_Description}
                  </Text>
                </Card>
              </div>

              {/* Variant */}
              {currentItem.tbl_ARRV_Reward_Variants?.length > 0 && (
                <div>
                  <Title level={5} style={{ marginBottom: 4, marginTop: 0 }}>Selected Variant</Title>
                  <Card size="small" style={{ background: '#fafafa' }}>
                    <Space size={[4, 8]} wrap>
                      {currentItem.tbl_ARRV_Reward_Variants.map(variant => (
                        variant.status && (
                          <Tag key={variant.ARRV_Reward_Variant_KeyID} color="blue">
                            {variant.ARRV_Title}
                          </Tag>
                        )
                      ))}
                    </Space>
                  </Card>
                </div>
              )}

            </Space>

            {/* Navigation Buttons - Fixed position */}
            <div style={{ 
              marginTop: 'auto',
              background: '#fff',
              padding: '16px 0',
              borderTop: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
            }}>
              <Button 
                onClick={prevItem}
                icon={<LeftOutlined />}
              >
                Previous Item
              </Button>
              <Button 
                onClick={nextItem}
                icon={<RightOutlined />}
              >
                Next Item
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

export default CartItemModal;